import { Box, IconButton, Typography } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import ClinicianDetailView from './details/ClinicianDetailView'
import ClinicianTable from './ClinicianTable'
import { DashboardControls } from './DashboardControls'
import PatientDetailView from './details/PatientDetailView'
import PatientTable from './PatientTable'
import { invalidateAllQueries } from '../../../util/queryClient'
import { Refresh } from '@mui/icons-material'
import DashboardGraph from './DashboardGraph'
import PageContainer from '../../../components/layout/PageContainer'
import useDashboardListFilters from '../../../util/useDashboardListFilters'

const Dashboard: React.FC = () => {
  const { filters, updateFilters, resetFilters } = useDashboardListFilters()
  const displayClinicianDetailView = !!filters.detailViewClinicianEmail
  const displayPatientDetailView = !!filters.detailViewPatientMrn
  const showTables = !displayClinicianDetailView && !displayPatientDetailView

  const previousClinicianFilter = useRef<string | undefined>(filters.userEmail)
  const previousPatientFilter = useRef<string | undefined>(filters.patientMrn)

  useEffect(() => {
    if (previousClinicianFilter.current !== filters.userEmail) {
      previousClinicianFilter.current = filters.userEmail
      if (filters.detailViewClinicianEmail) {
        updateFilters({ detailViewClinicianEmail: null })
      }
    }
  }, [filters.userEmail, filters.detailViewClinicianEmail, updateFilters])

  useEffect(() => {
    if (previousPatientFilter.current !== filters.patientMrn) {
      previousPatientFilter.current = filters.patientMrn
      if (filters.detailViewPatientMrn) {
        updateFilters({ detailViewPatientMrn: null })
      }
    }
  }, [filters.patientMrn, filters.detailViewPatientMrn, updateFilters])

  const handleRefresh = () => {
    resetFilters()
    invalidateAllQueries()
  }

  const renderDashboardContent = () => (
    <>
      <DashboardGraph />

      {/* Tables Section */}
      <Box sx={{ mt: 4 }}>
        {/* Detail Views - show when an item is selected */}
        {displayClinicianDetailView && <ClinicianDetailView />}

        {displayPatientDetailView && <PatientDetailView />}

        {/* Only show tables when no detail view is active */}
        {showTables && (
          <>
            <ClinicianTable />
            <PatientTable />
          </>
        )}

        {/* Details information */}
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          display="block"
          sx={{ mt: 2 }}
        >
          Clicking an item brings you to the details page
        </Typography>
      </Box>
    </>
  )

  return (
    <PageContainer maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom>
          Alerts Dashboard
        </Typography>
        <IconButton
          color="primary"
          onClick={handleRefresh}
          size="large"
          data-qa-id="dashboard-reset-button"
        >
          <Refresh />
        </IconButton>
      </Box>

      <DashboardControls />

      {renderDashboardContent()}
    </PageContainer>
  )
}

export default Dashboard
